import React, { useState, useRef, useEffect } from 'react';
import {
  IonPage, IonContent, IonIcon, IonSpinner,
  useIonToast,
} from '@ionic/react';
import {
  call, logoWhatsapp, mail, create, trash,
  arrowBack, ellipsisVertical, peopleOutline, chevronForward, locationOutline,
  documentOutline, navigate, calendarOutline, handLeft,
  alertCircle, checkmarkCircle,
} from 'ionicons/icons';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useIonRouter } from '@ionic/react';
import { format, parseISO } from 'date-fns';
import { ro } from 'date-fns/locale';
import {
  useApproveMember,
  useMember, useMemberNotes, useMemberHistory,
  useAddMemberNote, useUpdateMemberNote, useDeleteMemberNote,
  useDeleteMember, useChangeStatus,
} from '@/hooks/useMembers';
import { usePermission } from '@/hooks/usePermission';
import MemberConsentCard from '@/components/members/MemberConsentCard';
import MemberKindergartenCard from '@/components/members/MemberKindergartenCard';
import StatusChangeModal from '@/components/shared/StatusChangeModal';
import TransferLetterModal from '@/components/shared/TransferLetterModal';
import VisitsNeedsTabs from '@/components/visits/VisitsNeedsTabs';
import VisitModal from '@/components/visits/VisitModal';
import NeedModal from '@/components/visits/NeedModal';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import { useCreateVisit } from '@/hooks/useVisits';
import type { MemberStatusChange } from '@/types/member';
import {
  CHURCH_STATUS_LABELS, MEMBERSHIP_TYPE_LABELS, GENDER_LABELS,
  CIVIL_STATUS_LABELS, FAMILY_ROLE_LABELS, LABEL_FLAG_LABELS,
  PASTORAL_CASE_LABEL, isPastoralCase,
  GROUP_CATEGORY_COLORS,
} from '@/utils/labels';
import { getMemberCard, generateTransferLetter, downloadDocument, getMemberDocuments, type MemberDocType, type GeneratedDocRecord } from '@/api/documents';
import { downloadBlob } from '@/utils/download';
import { formatEffectiveAddress, googleMapsUrl } from '@/utils/address';

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  try { return format(parseISO(iso), 'd MMM yyyy', { locale: ro }); } catch { return iso; }
};
const cleanPhone = (p: string) => {
  const digits = p.replace(/\D/g, '');
  return digits.startsWith('0') ? '40' + digits.slice(1) : digits;
};

/* ── Reusable detail row ── */
const Row: React.FC<{ label: string; value?: string | null; children?: React.ReactNode }> = ({ label, value, children }) => {
  if (!value && !children) return null;
  return (
    <div className="detail-row">
      <span className="detail-row__label">{label}</span>
      <span className="detail-row__value">{children ?? value}</span>
    </div>
  );
};

const Badge: React.FC<{ text: string; color: string; bg: string }> = ({ text, color, bg }) => (
  <span style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, color, background: bg }}>{text}</span>
);

const statusStyle: Record<string, { color: string; bg: string }> = {
  active: { color: '#059669', bg: '#ecfdf5' },
  under_discipline: { color: '#d97706', bg: '#fef3c7' },
  transferred: { color: '#64748b', bg: '#f1f5f9' },
  left: { color: '#64748b', bg: '#f1f5f9' },
  excommunicated: { color: '#dc2626', bg: '#fef2f2' },
  deceased: { color: '#94a3b8', bg: '#f8fafc' },
};

/* ═══════════════════════════════════════════ */
/** Where the back button should land, handed over by whoever opened this page. */
interface BackTo { path: string; label: string }

const MemberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const router = useIonRouter();
  const navHistory = useHistory();
  const location = useLocation<{ backTo?: BackTo } | undefined>();
  const { can } = usePermission();

  // The referrer names itself (a family, a roster) so the label matches where
  // you actually came from. Popping the history entry — rather than pushing
  // the path — is what keeps the family page's own back button working.
  const backTo = location.state?.backTo;
  const backLabel = backTo?.label ?? 'Înapoi';
  const goBack = () => {
    if (navHistory.length > 1) navHistory.goBack();
    else router.push(backTo?.path ?? '/members', 'back');
  };
  const isAdmin = can('members.edit');
  // Spiritual editors may open the edit form too — the form and the API
  // restrict them to the spiritual-milestone fields.
  const canEdit = isAdmin || can('members.edit_spiritual');
  const canApprove = can('members.approve');
  const canViewSensitive = can('members.view_sensitive');
  const canCreateVisit = can('visits.create');
  const canCreateNeed = can('needs.create');
  const canGenerateDoc = can('documents.generate');
  const [present] = useIonToast();

  const validId = id && id !== 'new' ? id : undefined;
  const { data: member, isLoading, refetch } = useMember(validId);
  const { data: notes } = useMemberNotes(validId);
  const { data: history } = useMemberHistory(validId);
  const addNoteMut = useAddMemberNote();
  const updateNoteMut = useUpdateMemberNote();
  const deleteNoteMut = useDeleteMemberNote();
  const deleteMut = useDeleteMember();
  const changeStatusMut = useChangeStatus();
  const approveMut = useApproveMember();

  const [noteContent, setNoteContent] = useState('');
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState('');
  const [deleteNoteId, setDeleteNoteId] = useState<string | null>(null);
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [downloadingCard, setDownloadingCard] = useState<string | null>(null);
  const [showVisitModal, setShowVisitModal] = useState(false);
  const [showNeedModal, setShowNeedModal] = useState(false);
  const createVisitMut = useCreateVisit();
  const [docHistory, setDocHistory] = useState<GeneratedDocRecord[]>([]);
  const [docHistoryLoading, setDocHistoryLoading] = useState(false);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);

  const handleApprove = async () => {
    if (!id || approveMut.isPending) return;
    try {
      await approveMut.mutateAsync(id);
      present({ message: 'Persoana a fost aprobată.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      present({ message: 'Eroare la aprobare.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  };

  const handleQuickMemberVisit = async () => {
    if (!id || createVisitMut.isPending) return;
    try {
      await createVisitMut.mutateAsync({
        member_id: id,
        visit_date: new Date().toISOString().slice(0, 10),
        visit_type: 'pastorala',
        notes: null,
        has_special_need: false,
        special_need_type: null,
        special_need_notes: null,
      });
      present({ message: 'Vizită înregistrată.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      present({ message: 'Eroare la înregistrarea vizitei.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  };

  useEffect(() => {
    if (!validId || !canGenerateDoc) return;
    setDocHistoryLoading(true);
    getMemberDocuments(validId).then(setDocHistory).catch(() => {}).finally(() => setDocHistoryLoading(false));
  }, [validId, canGenerateDoc]);

  const handleRedownloadDoc = async (docId: string, meta: Record<string, string> | null) => {
    setDownloadingDocId(docId);
    try {
      const blob = await downloadDocument(docId);
      const name = meta?.member_name ? `adeverinta_transfer_${meta.member_name.replace(/\s+/g, '_')}.pdf` : 'adeverinta_transfer.pdf';
      downloadBlob(blob, name);
    } catch {
      present({ message: 'Eroare la descărcare.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally {
      setDownloadingDocId(null);
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node) &&
          menuBtnRef.current && !menuBtnRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handleDownloadCard = async (type: MemberDocType) => {
    if (!id || downloadingCard) return;
    setDownloadingCard(type);
    try {
      const blob = await getMemberCard(id, type);
      const suffix = type === 'card-simple' ? 'simpla' : 'completa';
      downloadBlob(blob, `fisa_membrala_${suffix}_${member?.last_name}_${member?.first_name}.pdf`);
    } catch {
      present({ message: 'Eroare la generarea documentului.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally {
      setDownloadingCard(null);
    }
  };

  const handleAddNote = async () => {
    if (!noteContent.trim() || !id) return;
    try {
      await addNoteMut.mutateAsync({ id, content: noteContent.trim() });
      setNoteContent('');
      present({ message: 'Notă adăugată.', duration: 2000, color: 'success', position: 'bottom' });
    } catch { present({ message: 'Eroare la adăugarea notei.', duration: 3000, color: 'danger', position: 'bottom' }); }
  };
  const handleDelete = async () => {
    if (!id) return;
    try {
      await deleteMut.mutateAsync(id);
      present({ message: 'Persoană ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
      router.push('/members', 'back');
    } catch { present({ message: 'Eroare la ștergere.', duration: 3000, color: 'danger', position: 'bottom' }); }
  };
  const handleStatusChange = async (data: MemberStatusChange) => {
    if (!id) return;
    try {
      await changeStatusMut.mutateAsync({ id, ...data });
      setShowStatusModal(false);
      present({ message: 'Status schimbat cu succes.', duration: 2000, color: 'success', position: 'bottom' });
      void refetch();
    } catch { present({ message: 'Eroare la schimbarea statusului.', duration: 3000, color: 'danger', position: 'bottom' }); }
  };

  const handleGenerateTransfer = async (data: {
    destination_church: string;
    destination_city?: string;
    decision_date?: string;
    special_mentions?: string;
    membership_period?: string;
  }) => {
    if (!id) return;
    setTransferLoading(true);
    try {
      const result = await generateTransferLetter(id, data);
      const docId = result.data.id;
      const blob = await downloadDocument(docId);
      downloadBlob(blob, `adeverinta_transfer_${member?.last_name}_${member?.first_name}.pdf`);
      setShowTransferModal(false);
      present({ message: 'Adeverință generată cu succes.', duration: 2000, color: 'success', position: 'bottom' });
      getMemberDocuments(id).then(setDocHistory).catch(() => {});
    } catch {
      present({ message: 'Eroare la generarea adeverinței.', duration: 3000, color: 'danger', position: 'bottom' });
    } finally {
      setTransferLoading(false);
    }
  };

  if (isLoading) return <IonPage><IonContent><div className="form-loading"><IonSpinner name="crescent" /></div></IonContent></IonPage>;
  if (!member && validId) return <IonPage><IonContent className="ion-padding"><p>Persoana nu a fost găsită.</p></IonContent></IonPage>;
  if (!member) return <IonPage><IonContent /></IonPage>;

  const st = statusStyle[member.church_status] ?? { color: '#059669', bg: '#ecfdf5' };
  const isInactive = ['transferred', 'left', 'excommunicated', 'deceased'].includes(member.church_status);
  const transferEntry = history?.find((h) => h.new_status === 'transferred' || h.new_status === 'left');
  const deceasedEntry = history?.find((h) => h.new_status === 'deceased');
  const labelFlags = [
    // Either cause reads as the same thing — turning 18 unbaptized moves you
    // into "fii risipitori" automatically, so there is no second chip.
    isPastoralCase(member) && PASTORAL_CASE_LABEL,
    member.has_disability && LABEL_FLAG_LABELS.has_disability,
    member.has_special_need && LABEL_FLAG_LABELS.has_special_need,
    member.no_membership && LABEL_FLAG_LABELS.no_membership,
  ].filter(Boolean) as string[];

  return (
    <IonPage>
      <IonContent>
        <div className="detail-page">

          {/* ── Top bar ── */}
          <div className="form-topbar">
            <button className="form-topbar__back" onClick={goBack}>
              <IonIcon icon={arrowBack} /> {backLabel}
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              {canEdit && (
                <button className="form-btn form-btn--small" onClick={() => router.push(`/members/${id}/edit`)}>
                  <IonIcon icon={create} /> Editează
                </button>
              )}
              {isAdmin && (
                <div style={{ position: 'relative' }}>
                  <button ref={menuBtnRef} className="form-topbar__back" onClick={() => setShowMenu(!showMenu)} style={{ padding: '6px 8px' }}>
                    <IonIcon icon={ellipsisVertical} style={{ fontSize: 20 }} />
                  </button>
                  {showMenu && (
                    <div ref={menuRef} className="crm-dropdown-menu">
                      <button className="crm-dropdown-item" onClick={() => { setShowMenu(false); router.push(`/members/${id}/edit`); }}>
                        <span className="crm-dropdown-icon"><IonIcon icon={create} /></span> Editează
                      </button>
                      {member.cell_phone && (
                        <>
                          <button className="crm-dropdown-item" onClick={() => { setShowMenu(false); window.open(`tel:${member.cell_phone}`, '_self'); }}>
                            <span className="crm-dropdown-icon"><IonIcon icon={call} /></span> Sună
                          </button>
                          <button className="crm-dropdown-item" onClick={() => { setShowMenu(false); window.open(`https://wa.me/${cleanPhone(member.cell_phone!)}`, '_blank'); }}>
                            <span className="crm-dropdown-icon"><IonIcon icon={logoWhatsapp} /></span> WhatsApp
                          </button>
                        </>
                      )}
                      <button className="crm-dropdown-item crm-dropdown-item--danger" onClick={() => { setShowMenu(false); setShowDeleteAlert(true); }}>
                        <span className="crm-dropdown-icon"><IonIcon icon={trash} /></span> Șterge
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Header ── */}
          <div className="detail-header">
            <div className="detail-header__left">
              <h1 className="detail-header__name">{member.last_name} {member.first_name}{member.middle_name ? ` ${member.middle_name}` : ''}</h1>
              <div className="detail-header__meta">
                <Badge text={MEMBERSHIP_TYPE_LABELS[member.membership_type]} color="#4f46e5" bg="#eef2ff" />
                {!member.is_approved ? <Badge text="Neaprobat" color="#b45309" bg="#fef3c7" /> : null}
                {member.sector_name ? <Badge text={member.sector_name} color="#5e81f4" bg="#eef2ff" /> : null}
                {member.age != null ? <span className="detail-header__age">{member.age} ani</span> : null}
                {labelFlags.length > 0 ? labelFlags.map((f) => <Badge key={f} text={f} color="#d97706" bg="#fef3c7" />) : null}
              </div>
            </div>

            {/* Right: operational status */}
            <div className="detail-header__status">
              <div className="detail-header__status-row">
                {member.church_status !== 'active' && (
                  <span className="detail-header__status-label" style={{ color: st.color }}>{CHURCH_STATUS_LABELS[member.church_status]}</span>
                )}
                {isAdmin && !isInactive ? (
                  <button className="detail-status-btn" onClick={() => setShowStatusModal(true)}>Schimbă</button>
                ) : null}
              </div>
              {(member.church_status === 'transferred' || member.church_status === 'left') && transferEntry ? (
                <div className="detail-header__status-detail">
                  {transferEntry.transfer_church ? <span>{transferEntry.transfer_church}{transferEntry.transfer_city ? `, ${transferEntry.transfer_city}` : ''}</span> : null}
                  {transferEntry.transfer_doc_id === 'cu_adeverinta'
                    ? <span style={{ fontSize: 11, color: '#059669' }}>Cu adeverință</span>
                    : <span style={{ fontSize: 11, color: '#94a3b8' }}>Fără adeverință</span>}
                  <span className="detail-header__status-date">{fmtDate(transferEntry.decision_date)}{transferEntry.reason ? ` · ${transferEntry.reason}` : ''}</span>
                </div>
              ) : null}
              {member.church_status === 'deceased' && deceasedEntry ? (
                <div className="detail-header__status-detail">
                  {deceasedEntry.death_date ? <span>Decedat: {fmtDate(deceasedEntry.death_date)}</span> : null}
                  <span className="detail-header__status-date">{fmtDate(deceasedEntry.decision_date)}</span>
                </div>
              ) : null}
              {member.church_status === 'excommunicated' && history?.[0] ? (
                <div className="detail-header__status-detail">
                  <span className="detail-header__status-date">{fmtDate(history[0].decision_date)}{history[0].reason ? ` · ${history[0].reason}` : ''}</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* ── Pending-approval banner ── */}
          {!member.is_approved && (
            <div className="detail-approval-banner">
              <div className="detail-approval-banner__text">
                <IonIcon icon={alertCircle} />
                <span>Această persoană a fost adăugată/modificată de un operator și așteaptă aprobare.</span>
              </div>
              {canApprove && (
                <button
                  type="button"
                  className="detail-approval-banner__btn"
                  onClick={handleApprove}
                  disabled={approveMut.isPending}
                >
                  <IonIcon icon={checkmarkCircle} />
                  {approveMut.isPending ? 'Se aprobă...' : 'Aprobă'}
                </button>
              )}
            </div>
          )}

          {/* ── Quick actions ── */}
          <div className="detail-header__actions">
              {/* Admin actions (visible on mobile where form-topbar is hidden) */}
              {canEdit && (
                <div className="detail-action-group detail-action-group--mobile-admin">
                  <span className="detail-action-group__label">Administrare</span>
                  <div className="detail-action-group__items">
                    <button type="button" className="detail-action" onClick={() => router.push(`/members/${id}/edit`)}>
                      <IonIcon icon={create} /> Editează
                    </button>
                    {isAdmin && (
                      <button type="button" className="detail-action detail-action--danger" onClick={() => setShowDeleteAlert(true)}>
                        <IonIcon icon={trash} /> Șterge
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Communication cluster */}
              {(member.cell_phone || member.email) ? (
                <div className="detail-action-group">
                  <span className="detail-action-group__label">Contact</span>
                  <div className="detail-action-group__items">
                    {member.cell_phone ? (
                      <>
                        <a href={`tel:${member.cell_phone}`} className="detail-action detail-action--call" title="Sună">
                          <IonIcon icon={call} /> Sună
                        </a>
                        <a href={`https://wa.me/${cleanPhone(member.cell_phone)}`} target="_blank" rel="noreferrer" className="detail-action detail-action--wa" title="WhatsApp">
                          <IonIcon icon={logoWhatsapp} /> WhatsApp
                        </a>
                      </>
                    ) : null}
                    {member.email ? (
                      <a href={`mailto:${member.email}`} className="detail-action" title="Email">
                        <IonIcon icon={mail} /> Email
                      </a>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {/* Location */}
              {(member.effective_address || member.effective_city) ? (() => {
                const effAddr = formatEffectiveAddress(member);
                return (
                  <div className="detail-action-group">
                    <span className="detail-action-group__label">Locație</span>
                    <div className="detail-action-group__items">
                      <a
                        href={googleMapsUrl({ latitude: member.effective_latitude, longitude: member.effective_longitude, text: effAddr })}
                        target="_blank"
                        rel="noreferrer"
                        className="detail-action detail-action--addr"
                      >
                        <IonIcon icon={navigate} /> {effAddr}
                      </a>
                    </div>
                  </div>
                );
              })() : null}

              {/* Vizite quick access */}
              {!isInactive && (canCreateVisit || canCreateNeed) ? (
                <div className="detail-action-group">
                  <span className="detail-action-group__label">Vizite & Nevoi</span>
                  <div className="detail-action-group__items">
                    {canCreateVisit && (
                      <button
                        type="button"
                        className="detail-action"
                        onClick={() => {
                          if (member.family_id) setShowVisitModal(true);
                          else void handleQuickMemberVisit();
                        }}
                        disabled={createVisitMut.isPending}
                      >
                        <IonIcon icon={calendarOutline} /> {createVisitMut.isPending ? 'Se salvează...' : 'Adaugă vizită'}
                      </button>
                    )}
                    {canCreateNeed && (
                      <button
                        type="button"
                        className="detail-action"
                        onClick={() => setShowNeedModal(true)}
                      >
                        <IonIcon icon={handLeft} /> Adaugă nevoie
                      </button>
                    )}
                  </div>
                </div>
              ) : null}

              {/* Documents cluster */}
              {canGenerateDoc && (
                <div className="detail-action-group">
                  <span className="detail-action-group__label">Documente</span>
                  <div className="detail-action-group__items">
                    <button className="detail-action detail-action--doc" onClick={() => handleDownloadCard('card-simple')} disabled={!!downloadingCard}>
                      {downloadingCard === 'card-simple' ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={documentOutline} />} Fișa Simplă
                    </button>
                    <button className="detail-action detail-action--doc" onClick={() => setShowTransferModal(true)}>
                      <IonIcon icon={documentOutline} /> Adeverință Transfer
                    </button>
                    {canViewSensitive ? (
                      <button className="detail-action detail-action--doc" onClick={() => handleDownloadCard('card-complete')} disabled={!!downloadingCard}>
                        {downloadingCard === 'card-complete' ? <IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> : <IonIcon icon={documentOutline} />} Fișa Completă
                      </button>
                    ) : null}
                  </div>
                </div>
              )}
            </div>

          {/* ── Actions separator ── */}
          <div style={{ borderBottom: '1px solid #f0f0f3', margin: '0 0 16px' }} />

          {/* ── Content: 2-column layout ── */}
          <div className="detail-grid">

            {/* LEFT COLUMN */}
            <div className="detail-col">

              {/* Date Personale */}
              <div className="detail-card">
                <h3 className="detail-card__title">Date Personale</h3>
                <Row label="Gen" value={GENDER_LABELS[member.gender]} />
                <Row label="Data nașterii">{member.birth_date ? `${fmtDate(member.birth_date)}${member.age != null ? ` (${member.age} ani)` : ''}` : null}</Row>
                <Row label="Locul nașterii" value={member.birth_place} />
                <Row label="Stare civilă" value={member.civil_status ? CIVIL_STATUS_LABELS[member.civil_status] : null} />
                <Row label="Ocupație" value={member.occupation} />
                <Row label="Telefon mobil">{member.cell_phone ? <a href={`tel:${member.cell_phone}`} style={{ color: '#5e81f4', textDecoration: 'none' }}>{member.cell_phone}</a> : null}</Row>
                <Row label="Telefon fix">{member.home_phone ? <a href={`tel:${member.home_phone}`} style={{ color: '#5e81f4', textDecoration: 'none' }}>{member.home_phone}</a> : null}</Row>
                <Row label="Email">{member.email ? <a href={`mailto:${member.email}`} style={{ color: '#5e81f4', textDecoration: 'none' }}>{member.email}</a> : null}</Row>
                {(member.effective_address || member.effective_city) && (() => {
                  const display = formatEffectiveAddress(member);
                  return (
                    <Row label="Adresa">
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <a
                          href={googleMapsUrl({ latitude: member.effective_latitude, longitude: member.effective_longitude, text: display })}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#5e81f4', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <IonIcon icon={navigate} style={{ fontSize: 13 }} />
                          {display}
                        </a>
                        {member.address_source === 'personal' && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 4,
                              padding: '2px 8px 2px 6px',
                              fontSize: 9.5,
                              fontWeight: 700,
                              letterSpacing: '0.08em',
                              textTransform: 'uppercase',
                              color: '#5e81f4',
                              background: 'rgba(94, 129, 244, 0.08)',
                              border: '1px solid rgba(94, 129, 244, 0.2)',
                              borderRadius: 999,
                              lineHeight: 1,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#5e81f4', boxShadow: '0 0 0 2px rgba(94,129,244,0.15)' }} />
                            personală
                          </span>
                        )}
                      </span>
                    </Row>
                  );
                })()}
              </div>

              {/* Familie */}
              <div className="detail-card">
                <h3 className="detail-card__title">Familie</h3>
                {member.family_id ? (
                  <>
                    <div className="detail-family-link" onClick={() => router.push(`/families/${member.family_id}`)}>
                      <IonIcon icon={peopleOutline} style={{ fontSize: 20, color: '#5e81f4' }} />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1c1d21' }}>{member.family_name ?? 'Familie'}</div>
                        {member.family_role && <div style={{ fontSize: 12, color: '#64748b' }}>{FAMILY_ROLE_LABELS[member.family_role]}</div>}
                      </div>
                      <IonIcon icon={chevronForward} style={{ marginLeft: 'auto', color: '#94a3b8' }} />
                    </div>
                    {(member.effective_address || member.effective_city) && (() => {
                      const fullAddr = formatEffectiveAddress(member);
                      const mapsUrl = googleMapsUrl({ latitude: member.effective_latitude, longitude: member.effective_longitude, text: fullAddr });
                      return (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 8, marginTop: 8,
                            padding: '8px 10px', background: '#f8fafc', borderRadius: 8,
                            textDecoration: 'none', cursor: 'pointer', transition: 'background 0.15s',
                          }}
                          onMouseOver={(e) => (e.currentTarget.style.background = '#eef2ff')}
                          onMouseOut={(e) => (e.currentTarget.style.background = '#f8fafc')}
                        >
                          <IonIcon icon={locationOutline} style={{ fontSize: 14, color: '#5e81f4', marginTop: 1, flexShrink: 0 }} />
                          <span style={{ fontSize: 13, color: '#5e81f4', lineHeight: 1.4, flex: 1 }}>{fullAddr}</span>
                          <IonIcon icon={navigate} style={{ fontSize: 14, color: '#5e81f4', flexShrink: 0 }} />
                        </a>
                      );
                    })()}
                  </>
                ) : <div style={{ fontSize: 13, color: '#94a3b8', padding: '4px 0' }}>Nu este asociat(ă) unei familii.</div>}
              </div>

              {/* Date Sensibile (admin) */}
              {canViewSensitive && (member.spiritual_state || member.material_state || member.health_state || member.special_case) && (
                <div className="detail-card detail-card--sensitive">
                  <h3 className="detail-card__title" style={{ color: '#dc2626' }}>Date Sensibile</h3>
                  <Row label="Stare spirituală" value={member.spiritual_state} />
                  <Row label="Stare materială" value={member.material_state} />
                  <Row label="Stare sănătate" value={member.health_state} />
                  <Row label="Caz special" value={member.special_case} />
                </div>
              )}

              {/* GDPR Consimțământ — admin/pastor/secretary concern only */}
              {isAdmin && <MemberConsentCard memberId={id} isAdmin={isAdmin} />}

              {can('kindergarten.view') && <MemberKindergartenCard memberId={id} />}
            </div>

            {/* RIGHT COLUMN */}
            <div className="detail-col">

              {/* Etape Spirituale */}
              <div className="detail-card">
                <h3 className="detail-card__title">Etape Spirituale</h3>
                <Row label="Binecuvântare">{member.blessing_date ? `${fmtDate(member.blessing_date)}${member.blessing_place ? ` — ${member.blessing_place}` : ''}` : null}</Row>
                {member.blessing_by && <Row label="Oficiant binecuvântare" value={member.blessing_by} />}
                <Row label="Botez în apă">{member.water_baptism_date ? `${fmtDate(member.water_baptism_date)}${member.water_baptism_place ? ` — ${member.water_baptism_place}` : ''}` : null}</Row>
                {member.water_baptism_by && <Row label="Oficiant botez" value={member.water_baptism_by} />}
                <Row label="Botez cu Duhul Sfânt">{member.spirit_baptism ? (member.spirit_baptism_date ? `Da — ${fmtDate(member.spirit_baptism_date)}${member.spirit_baptism_place ? ` — ${member.spirit_baptism_place}` : ''}` : 'Da') : 'Nu'}</Row>
                <Row label="Căsătorie">{member.wedding_date ? `${fmtDate(member.wedding_date)}${member.wedding_place ? ` — ${member.wedding_place}` : ''}` : null}</Row>
                {member.wedding_by && <Row label="Oficiant căsătorie" value={member.wedding_by} />}
                <Row label="Primire ca membru">{member.membership_date ? `${fmtDate(member.membership_date)}${member.membership_place ? ` — ${member.membership_place}` : ''}` : null}</Row>
              </div>

              {/* Grupuri / Implicare în biserică */}
              <div className="detail-card">
                <h3 className="detail-card__title">Implicare în Biserică {member.groups.length > 0 && `(${member.groups.length})`}</h3>
                {member.groups.length > 0 ? (
                  member.groups.map((g) => {
                    const catColor = (GROUP_CATEGORY_COLORS as Record<string, string>)[g.category ?? ''] ?? '#64748b';
                    return (
                      <div key={g.id} className="detail-group-item" onClick={() => router.push(`/groups/${g.id}`)}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: catColor, flexShrink: 0 }} />
                          <span style={{ fontWeight: 500 }}>{g.name}</span>
                        </div>
                        {g.role && <Badge text={g.role === 'leader' ? 'Lider' : 'Membru'} color={g.role === 'leader' ? '#4f46e5' : '#94a3b8'} bg={g.role === 'leader' ? '#eef2ff' : '#f8fafc'} />}
                      </div>
                    );
                  })
                ) : (
                  <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>Nu este implicat(ă) în niciun grup.</p>
                )}
              </div>

              {/* Observații */}
              {member.notes && (
                <div className="detail-card">
                  <h3 className="detail-card__title">Observații</h3>
                  <p style={{ fontSize: 13, color: '#475569', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.5 }}>{member.notes}</p>
                </div>
              )}

              {/* Note */}
              <div className="detail-card">
                <h3 className="detail-card__title">Note {notes && notes.length > 0 ? `(${notes.length})` : ''}</h3>
                <div style={{ marginBottom: notes && notes.length > 0 ? 12 : 0 }}>
                  <textarea
                    className="form-textarea"
                    value={noteContent}
                    onChange={(e) => setNoteContent(e.target.value)}
                    placeholder="Scrie o notă despre această persoană... (vizite, observații, rugăciuni)"
                    style={{ width: '100%', minHeight: 60, resize: 'vertical', marginBottom: 8 }}
                    disabled={addNoteMut.isPending}
                    onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) { void handleAddNote(); } }}
                  />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: '#94a3b8' }}>Ctrl+Enter pentru a adăuga</span>
                    <button className="form-btn form-btn--primary" onClick={handleAddNote}
                      disabled={!noteContent.trim() || addNoteMut.isPending}
                      style={{ padding: '8px 16px', minWidth: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                      {addNoteMut.isPending ? <><IonSpinner name="crescent" style={{ width: 14, height: 14 }} /> Se adaugă...</> : 'Adaugă notă'}
                    </button>
                  </div>
                </div>
                {notes?.map((n: any) => (
                  <div key={n.id} className="detail-note">
                    {editingNoteId === n.id ? (
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        <input
                          className="form-input"
                          value={editingNoteContent}
                          onChange={(e) => setEditingNoteContent(e.target.value)}
                          style={{ flex: 1, fontSize: 13 }}
                          disabled={updateNoteMut.isPending}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editingNoteContent.trim()) {
                              void updateNoteMut.mutateAsync({ memberId: id, noteId: n.id, content: editingNoteContent.trim() }).then(() => {
                                setEditingNoteId(null);
                                present({ message: 'Notă actualizată.', duration: 2000, color: 'success', position: 'bottom' });
                              }).catch(() => {
                                present({ message: 'Eroare la actualizare. Încearcă din nou după deploy.', duration: 3000, color: 'danger', position: 'bottom' });
                              });
                            }
                            if (e.key === 'Escape') setEditingNoteId(null);
                          }}
                          autoFocus
                        />
                        <button
                          className="form-btn form-btn--small form-btn--primary"
                          style={{ padding: '6px 12px', fontSize: 12, minWidth: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          disabled={!editingNoteContent.trim() || updateNoteMut.isPending}
                          onClick={() => {
                            if (!editingNoteContent.trim()) return;
                            void updateNoteMut.mutateAsync({ memberId: id, noteId: n.id, content: editingNoteContent.trim() }).then(() => {
                              setEditingNoteId(null);
                              present({ message: 'Notă actualizată.', duration: 2000, color: 'success', position: 'bottom' });
                            }).catch(() => {
                              present({ message: 'Eroare la actualizare. Încearcă din nou după deploy.', duration: 3000, color: 'danger', position: 'bottom' });
                            });
                          }}
                        >
                          {updateNoteMut.isPending ? <IonSpinner name="crescent" style={{ width: 12, height: 12 }} /> : 'OK'}
                        </button>
                        <button
                          className="form-btn form-btn--small"
                          style={{ padding: '6px 10px', fontSize: 14 }}
                          onClick={() => setEditingNoteId(null)}
                        >
                          ✕
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                          <p style={{ margin: 0, fontSize: 13, color: '#1c1d21', flex: 1 }}>
                            {typeof n.content === 'string' ? n.content : JSON.stringify(n.content)}
                          </p>
                          {isAdmin && (
                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                              <button
                                onClick={() => { setEditingNoteId(n.id); setEditingNoteContent(typeof n.content === 'string' ? n.content : ''); }}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                                title="Editează"
                              >
                                <IonIcon icon={create} style={{ fontSize: 14, color: '#94a3b8' }} />
                              </button>
                              <button
                                onClick={() => setDeleteNoteId(n.id)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                                title="Șterge"
                              >
                                <IonIcon icon={trash} style={{ fontSize: 14, color: '#dc2626' }} />
                              </button>
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: '#94a3b8' }}>{n.author_name ?? n.user?.name ?? '—'} · {fmtDate(n.created_at)}</span>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Vizite & Nevoi */}
              {!isInactive ? (
                <>
                  {member.family_id ? (
                    <VisitsNeedsTabs familyId={member.family_id} memberId={id} context="member" />
                  ) : (
                    <VisitsNeedsTabs memberId={id} context="member" />
                  )}
                </>
              ) : (
                <div className="detail-card">
                  <h3 className="detail-card__title">Vizite & Nevoi</h3>
                  <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                    Vizitele și nevoile nu mai sunt active pentru persoanele cu status „{CHURCH_STATUS_LABELS[member.church_status]}".
                  </p>
                </div>
              )}

              {/* Istoric Statusuri */}
              {history && history.length > 0 && (
                <div className="detail-card">
                  <h3 className="detail-card__title">Istoric Statusuri</h3>
                  {history.map((h) => {
                    const hst = statusStyle[h.new_status] ?? { color: '#64748b', bg: '#f1f5f9' };
                    return (
                      <div key={h.id} className="detail-history-entry" style={{ borderLeftColor: hst.color }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <Badge
                            text={h.old_status ? `${CHURCH_STATUS_LABELS[h.old_status as keyof typeof CHURCH_STATUS_LABELS] ?? h.old_status} → ${CHURCH_STATUS_LABELS[h.new_status as keyof typeof CHURCH_STATUS_LABELS] ?? h.new_status}` : CHURCH_STATUS_LABELS[h.new_status as keyof typeof CHURCH_STATUS_LABELS] ?? h.new_status}
                            color={hst.color}
                            bg={hst.bg}
                          />
                        </div>
                        {h.reason ? <p style={{ margin: '0 0 4px', fontSize: 13, color: '#475569' }}>{h.reason}</p> : null}
                        {h.transfer_church ? (
                          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#475569' }}>
                            Biserica: <strong>{h.transfer_church}</strong>
                            {h.transfer_city ? `, ${h.transfer_city}` : ''}
                          </p>
                        ) : null}
                        {h.death_date ? (
                          <p style={{ margin: '0 0 4px', fontSize: 13, color: '#475569' }}>
                            Data decesului: <strong>{fmtDate(h.death_date)}</strong>
                          </p>
                        ) : null}
                        {h.notes ? <p style={{ margin: '0 0 4px', fontSize: 12, color: '#64748b', fontStyle: 'italic' }}>{h.notes}</p> : null}
                        <span style={{ fontSize: 11, color: '#64748b' }}>{h.decided_by_name} · {fmtDate(h.decision_date)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Documente Generate */}
              {canGenerateDoc && docHistory.length > 0 && (
                <div className="detail-card">
                  <h3 className="detail-card__title">Documente Generate ({docHistory.length})</h3>
                  {docHistoryLoading ? (
                    <div style={{ textAlign: 'center', padding: 12 }}><IonSpinner name="crescent" style={{ width: 20, height: 20 }} /></div>
                  ) : (
                    docHistory.map((doc) => (
                      <div key={doc.id} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '8px 0', borderBottom: '1px solid #f1f5f9',
                      }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 500, color: '#1c1d21' }}>
                            Adeverință Transfer
                            {doc.metadata?.destination_church ? ` → ${doc.metadata.destination_church}` : ''}
                          </div>
                          <span style={{ fontSize: 11, color: '#94a3b8' }}>{fmtDate(doc.created_at)}</span>
                        </div>
                        <button
                          onClick={() => handleRedownloadDoc(doc.id, doc.metadata)}
                          disabled={downloadingDocId === doc.id}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            background: '#f1f5f9', border: 'none', borderRadius: 6,
                            padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#475569',
                          }}
                        >
                          {downloadingDocId === doc.id
                            ? <IonSpinner name="crescent" style={{ width: 12, height: 12 }} />
                            : <IonIcon icon={documentOutline} style={{ fontSize: 14 }} />}
                          Descarcă
                        </button>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <StatusChangeModal isOpen={showStatusModal} currentStatus={member.church_status}
          onDismiss={() => setShowStatusModal(false)} onConfirm={handleStatusChange} isLoading={changeStatusMut.isPending} />

        <TransferLetterModal
          isOpen={showTransferModal}
          memberName={member.full_name}
          onDismiss={() => setShowTransferModal(false)}
          onConfirm={handleGenerateTransfer}
          isLoading={transferLoading}
          defaultChurch={history?.find((h) => h.new_status === 'transferred')?.transfer_church ?? ''}
          defaultCity={history?.find((h) => h.new_status === 'transferred')?.transfer_city ?? ''}
          waterBaptismDate={member.water_baptism_date}
        />

        <ConfirmDialog
          isOpen={showDeleteAlert}
          title="Ștergere Persoană"
          message={`Ești sigur că vrei să ștergi ${member.full_name}? Poate fi restaurat ulterior.`}
          confirmLabel="Șterge"
          variant="danger"
          isLoading={deleteMut.isPending}
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteAlert(false)}
        />

        <VisitModal
          isOpen={showVisitModal}
          onClose={() => setShowVisitModal(false)}
          familyId={member.family_id ?? undefined}
        />

        <NeedModal
          isOpen={showNeedModal}
          onClose={() => setShowNeedModal(false)}
          familyId={member.family_id ?? undefined}
          memberId={id}
        />

        <ConfirmDialog
          isOpen={!!deleteNoteId}
          title="Ștergere Notă"
          message="Ești sigur că vrei să ștergi această notă?"
          confirmLabel="Șterge"
          variant="danger"
          isLoading={deleteNoteMut.isPending}
          onConfirm={() => {
            if (!deleteNoteId) return;
            void deleteNoteMut.mutateAsync({ memberId: id, noteId: deleteNoteId }).then(() => {
              setDeleteNoteId(null);
              present({ message: 'Notă ștearsă.', duration: 2000, color: 'success', position: 'bottom' });
            }).catch(() => {
              present({ message: 'Eroare la ștergere.', duration: 3000, color: 'danger', position: 'bottom' });
            });
          }}
          onCancel={() => setDeleteNoteId(null)}
        />
      </IonContent>
    </IonPage>
  );
};

export default MemberDetailPage;
